import * as Yup from "yup";
import { toast } from "react-toastify";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Form, Formik, Field, ErrorMessage } from "formik";
import {
  CameraIcon,
  FbIcon,
  InstaIcon,
  TikTokIcon,
  TwitterIcon,
  UtubeIcon,
} from "../../../Assets/svgIcons";
import { getAuthMapping } from "../../../Utils/authmapping.utils";
import {
  numberFormatInfluencer,
  socialMediaPatterns,
} from "../../../Utils/common.utils";
import Loading from "../../../components/loading/loading.component";

const Step1 = ({ setStep, profileData, stepOneData, setStepOneData }) => {
  // ********** STATES **********
  const { state } = useLocation();
  const navigate = useNavigate();
  const localStorageData = getAuthMapping();
  const [loading, setLoading] = useState(false);

  // ******* FUNCTION FOR SOCIAL MEDIA NUMBER VALIDATION *******
  const numberValidation = () =>
    Yup.string()
      .nullable()
      .test("max-value", "Must be at most 1 billion", (value) => {
        if (!value) return true;
        return parseInt(value.replace(/,/g, ""), 10) <= 1_000_000_000;
      })
      .test("is-number", "Only numbers are allowed", (value) => {
        if (!value) return true;
        return /^\d+$/.test(value.replace(/,/g, ""));
      })
      .test("min-value", "Value must be greater than 0", (value) => {
        if (!value) return true;
        return parseInt(value.replace(/,/g, ""), 10) > 0;
      });

  // ********** ENROLMENT INITIAL VALUES **********
  const enrollmentValues = {
    image: stepOneData?.image || profileData?.influencer_meta?.image || "",
    description:
      stepOneData?.description ||
      profileData?.influencer_meta?.description ||
      "",
    youtubeViews: numberFormatInfluencer(
      stepOneData?.youtubeViews ||
        profileData?.influencer_socials?.youtube_monthly_views ||
        ""
    ),
    facebookViews: numberFormatInfluencer(
      stepOneData?.facebookViews ||
        profileData?.influencer_socials?.facebook_followers ||
        ""
    ),
    instagramViews: numberFormatInfluencer(
      stepOneData?.instagramViews ||
        profileData?.influencer_socials?.instagram_followers ||
        ""
    ),
    twitterViews: numberFormatInfluencer(
      stepOneData?.twitterViews ||
        profileData?.influencer_socials?.twitter_followers ||
        ""
    ),
    tiktokViews: numberFormatInfluencer(
      stepOneData?.tiktokViews ||
        profileData?.influencer_socials?.tiktok_followers ||
        ""
    ),
    youtubeChannelName:
      stepOneData?.youtubeChannelName ||
      profileData?.influencer_socials?.youtube_channel_name ||
      "",
    facebookChannelName:
      stepOneData?.facebookChannelName ||
      profileData?.influencer_socials?.facebook_channel_name ||
      "",
    instagramChannelName:
      stepOneData?.instagramChannelName ||
      profileData?.influencer_socials?.instagram_channel_name ||
      "",
    twitterChannelName:
      stepOneData?.twitterChannelName ||
      profileData?.influencer_socials?.twitter_channel_name ||
      "",
    tiktokChannelName:
      stepOneData?.tiktokChannelName ||
      profileData?.influencer_socials?.tiktok_channel_name ||
      "",
  };

  //  ********** Enrolment Schema **********
  const enrollmentSchema = Yup.object({
    image: Yup.string(),
    // .required("Image is Required"),
    description: Yup.string()
      .required("Description is Required")
      .min(1)
      .max(500, "Description should have a maximum length of 500 characters"),
    youtubeViews: numberValidation(),
    facebookViews: numberValidation(),
    instagramViews: numberValidation(),
    twitterViews: numberValidation(),
    tiktokViews: numberValidation(),
    youtubeChannelName: Yup.string()
      .min(1)
      .max(100, "Channel Name should have a maximum length of 100 characters")
      .test("is-valid-url", "Invalid Youtube URL", (value) =>
        value ? value.match(socialMediaPatterns.youtube) : true
      ),
    facebookChannelName: Yup.string()
      .min(1)
      .max(100, "Channel Name should have a maximum length of 100 characters")
      .test("is-valid-url", "Invalid Facebook URL", (value) =>
        value ? value.match(socialMediaPatterns.facebook) : true
      ),
    instagramChannelName: Yup.string()
      .min(1)
      .max(100, "Channel Name should have a maximum length of 100 characters")
      .test("is-valid-url", "Invalid Instagram URL", (value) =>
        value ? value.match(socialMediaPatterns.instagram) : true
      ),
    twitterChannelName: Yup.string()
      .min(1)
      .max(100, "Channel Name should have a maximum length of 100 characters")
      .test("is-valid-url", "Invalid Twitter URL", (value) =>
        value ? value.match(socialMediaPatterns.twitter) : true
      ),
    tiktokChannelName: Yup.string()
      .min(1)
      .max(100, "Channel Name should have a maximum length of 100 characters")
      .test("is-valid-url", "Invalid Tiktok URL", (value) =>
        value ? value.match(socialMediaPatterns.tiktok) : true
      )
  });

  // // ********** SUBMIT FUNCTION **********
  const onSubmit = async (values) => {
    try {
      if (values) {
        const formattedValues = { ...values };
        // Remove commas from numeric fields
        formattedValues.youtubeViews = Number(
          formattedValues?.youtubeViews?.replace(/,/g, "")
        );
        formattedValues.facebookViews = Number(
          formattedValues?.facebookViews?.replace(/,/g, "")
        );
        formattedValues.instagramViews = Number(
          formattedValues?.instagramViews?.replace(/,/g, "")
        );
        formattedValues.twitterViews = Number(
          formattedValues?.twitterViews?.replace(/,/g, "")
        );
        formattedValues.tiktokViews = Number(
          formattedValues?.tiktokViews?.replace(/,/g, "")
        );
        setStepOneData({
          ...formattedValues,
          re_apply_comment: state || profileData?.influencer_meta?.re_apply_comment,
        });
        setStep((prev) => prev + 1);
      }
    } catch (error) {
      toast.error(error?.message);
      console.log(error, "error");
    }
  };

  // ==================== UPLOAD INFLUENCER IMAGE ====================
  const handleImage = async (e, formik) => {
    try {
      const file = e.target.files[0];

      // ### VALIDATION EXTENSION ALLOWED JPG , PNG , GIF ####
      const allowedTypes = ["image/jpeg", "image/png", "image/gif"];
      if (!allowedTypes.includes(file.type)) {
        toast.error(
          `Invalid file type. Allowed types are: ${allowedTypes
            .map((type) => type.split("/")[1])
            .join(", ")}.`
        );
        return;
      }

      // ### VALIDATION SIZE ALLOWED 5 MB ####
      const maxFileSizeMB = 5;
      const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;
      if (file.size > maxFileSizeBytes) {
        toast.error(`File size exceeds the ${maxFileSizeMB}MB limit.`);
        return;
      }

      const formDataNew = new FormData();
      formDataNew.append("file", file);
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_STAGE_API_URL}users/v1/user/upload-influencer-profile-image`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorageData?.accessToken}` },
          body: formDataNew,
        }
      );
      if (!response.ok) {
        setLoading(false);
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      if (data?.status === 200) {
        formik.setFieldValue("image", data?.data?.image_url || "");
        setLoading(false);
      }
      return data;
    } catch (error) {
      setLoading(false);
      console.error("API fetch error:", error.message);
    }
  };
  // ==================== VALIDATION OF SOCIAL INPUTS ====================
  const handleNumberInput = (e) => {
    const invalidKeys = ["-", "+", ".", "e", " "];
    if (invalidKeys.includes(e.key) || e.keyCode === 32) {
      e.preventDefault();
    }
  };

  // const handleNumberChange = (e) => {
  //   e.target.value = e.target.value
  //     .replace(/[^0-9]/g, "")
  //     .replace(/^0+(\d)/, "$1");
  // };

  const handleNumberChange = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, "");
    value = value.replace(/^0+/, "");
    if (value) {
      // Format the number with commas
      const formattedValue = parseInt(value, 10).toLocaleString("en-US");
      e.target.value = formattedValue;
    } else {
      e.target.value = "";
    }
  };

  return (
    <Formik
      initialValues={enrollmentValues}
      validationSchema={enrollmentSchema}
      onSubmit={onSubmit}
      validateOnChange
      enableReinitialize
      validate={(values) => {
        const errors = {};
        const platforms = [
          {
            views: values?.youtubeViews,
            channel: values?.youtubeChannelName,
            field: "youtubeChannelName",
          },
          {
            views: values?.facebookViews,
            channel: values?.facebookChannelName,
            field: "facebookChannelName",
          },
          {
            views: values?.instagramViews,
            channel: values?.instagramChannelName,
            field: "instagramChannelName",
          },
          {
            views: values?.twitterViews,
            channel: values?.twitterChannelName,
            field: "twitterChannelName",
          },
          {
            views: values?.tiktokViews,
            channel: values?.tiktokChannelName,
            field: "tiktokChannelName",
          },
        ];
        const isValid = platforms.some(
          ({ views, channel }) => views?.trim() && channel?.trim()
        );
        if (!isValid) {
          platforms.forEach(({ field }) => {
            errors[field] =
              "At least one Social Media platform must have both followers and a channel name.";
          });
        }
        platforms.forEach(({ views, channel, field }) => {
          if (views?.trim() && !channel?.trim()) {
            errors[field] =
              "Channel name is required when followers are provided.";
          }
        });
        return errors;
      }}
    >
      {(formik) => {
        return (
          <Form>
            <h4>Unlock Your Earning Potential with MusicBull!</h4>
            <p>
              As an influencer, you have a unique opportunity to monetize your
              network while empowering artists on their journey toward financial
              and artistic freedom. By promoting MusicBull, you’re not just
              supporting the music industry—you’re enabling fans and investors
              to earn attractive financial returns while uplifting the artists
              they love.
            </p>
            <h4>Why Join MusicBull as an Influencer?</h4>
            <p>
              By referring users to the MusicBull trading platform, you can
              create a growing revenue stream. You'll earn rewards not just from
              specific auctions but also from every secondary trading
              transaction your referred users execute.
            </p>
            <div className="profile_img_uploader">
              {/* <label>
                {open && (
                  <UploadFileContactUS
                    open={open}
                    handleClose={handleClose}
                    title={"Upload File"}
                    setFileUrl={(e) => {
                      formik.setFieldValue("image", e?.fileAbsoluteUrl||'');
                    }}
                  />
                )}
                {formik?.values?.image ? (
                  <>
                    <img src={formik?.values?.image} alt="profile" />
                    <button
                      onClick={() => formik.setFieldValue("image", "")}
                      className="cros_btn"
                    >
                      X
                    </button>
                  </>
                ) : (
                  <>
                    <span onClick={() => setOpen(true)}>
                      <CameraIcon />
                    </span>
                    Photo
                  </>
                )}
              </label> */}
              {loading ? (
                <label className="loading_label">
                  <Loading />
                </label>
              ) : (
                <label>
                  {formik?.values?.image ? (
                    <>
                      <img src={formik?.values?.image} alt="profile" />
                      <button
                        onClick={() => formik.setFieldValue("image", "")}
                        className="cros_btn"
                      >
                        X
                      </button>
                    </>
                  ) : (
                    <>
                      <input
                        type="file"
                        onChange={(e) => handleImage(e, formik)}
                      />
                      <span>
                        <CameraIcon />
                      </span>
                      Photo
                    </>
                  )}
                </label>
              )}

              <ErrorMessage name="image" component="div" className="error" />
            </div>
            <div className="desc_box">
              <label htmlFor="desc">Enter a short description about you </label>
              <Field
                as="textarea"
                id="description"
                name="description"
                placeholder=""
              />
              <ErrorMessage
                name="description"
                component="div"
                className="error"
              />
            </div>
            <hr className="custom_hr" />
            <div className="social_media">
              <label>Enter your social network information</label>
              <ul>
                <li>
                  <div className="social_media_card">
                    <UtubeIcon />
                    <div>
                      <Field
                        type="text"
                        name="youtubeViews"
                        className="views_input"
                        onInput={handleNumberChange}
                        onKeyDown={handleNumberInput}
                      />
                      <ErrorMessage
                        name="youtubeViews"
                        component="div"
                        className="error"
                      />
                    </div>
                    <label>Monthly Views</label>
                    <div>
                      <Field
                        type="text"
                        name="youtubeChannelName"
                        placeholder="Channel name"
                      />
                      <ErrorMessage
                        name="youtubeChannelName"
                        component="div"
                        className="error"
                      />
                    </div>
                  </div>
                </li>
                <li>
                  <div className="social_media_card">
                    <InstaIcon />
                    <div>
                      <Field
                        type="text"
                        name="instagramViews"
                        className="views_input"
                        onInput={handleNumberChange}
                        onKeyDown={handleNumberInput}
                      />
                      <ErrorMessage
                        name="instagramViews"
                        component="div"
                        className="error"
                      />
                    </div>
                    <label>Followers</label>
                    <div>
                      <Field
                        type="text"
                        name="instagramChannelName"
                        placeholder="Channel name"
                      />
                      <ErrorMessage
                        name="instagramChannelName"
                        component="div"
                        className="error"
                      />
                    </div>
                  </div>
                </li>
                <li>
                  <div className="social_media_card">
                    <FbIcon />
                    <div>
                      <Field
                        type="text"
                        name="facebookViews"
                        className="views_input"
                        onInput={handleNumberChange}
                        onKeyDown={handleNumberInput}
                      />
                      <ErrorMessage
                        name="facebookViews"
                        component="div"
                        className="error"
                      />
                    </div>
                    <label>Followers</label>
                    <div>
                      <Field
                        type="text"
                        name="facebookChannelName"
                        placeholder="Channel name"
                      />
                      <ErrorMessage
                        name="facebookChannelName"
                        component="div"
                        className="error"
                      />
                    </div>
                  </div>
                </li>
                <li>
                  <div className="social_media_card">
                    <TwitterIcon />
                    <div>
                      <Field
                        type="text"
                        name="twitterViews"
                        className="views_input"
                        onInput={handleNumberChange}
                        onKeyDown={handleNumberInput}
                      />
                      <ErrorMessage
                        name="twitterViews"
                        component="div"
                        className="error"
                      />
                    </div>
                    <label>Followers</label>
                    <div>
                      <Field
                        type="text"
                        name="twitterChannelName"
                        placeholder="Channel name"
                      />
                      <ErrorMessage
                        name="twitterChannelName"
                        component="div"
                        className="error"
                      />
                    </div>
                  </div>
                </li>
                <li>
                  <div className="social_media_card">
                    <TikTokIcon />
                    <div>
                      <Field
                        type="text"
                        name="tiktokViews"
                        className="views_input"
                        onInput={handleNumberChange}
                        onKeyDown={handleNumberInput}
                      />
                      <ErrorMessage
                        name="tiktokViews"
                        component="div"
                        className="error"
                      />
                    </div>
                    <label>Followers</label>
                    <div>
                      <Field
                        type="text"
                        name="tiktokChannelName"
                        placeholder="Channel name"
                      />
                      <ErrorMessage
                        name="tiktokChannelName"
                        component="div"
                        className="error"
                      />
                    </div>
                  </div>
                </li>
              </ul>
            </div>
            <button type="submit" className="submit_btn">
              Next
            </button>{" "}
            {profileData?.influencer_request === null && (
              <button
                type="button"
                className="submit_btn"
                onClick={() => navigate("/auctions-board")}
              >
                Skip
              </button>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default Step1;
